import { useEffect, useRef, useState } from 'react';
import type { CartItem } from '../page';
import { fetchGuestOrderStatus } from '@/lib/data';
import { registerPush } from '@/lib/push';

interface OrderConfirmedProps {
  orderId: string;
  /** Secret token returned by place_self_order — the only way a guest can read
      their own order status (guests have no SELECT on orders). */
  accessToken: string;
  restaurantId: string;
  cart: CartItem[];
  gstRate: number;
  gstInclusive?: boolean;
  onBackToMenu: () => void;
}

/** How often the diner's tracker asks the server for a status change. Polling
    (not realtime) because a guest's socket is anonymous and RLS won't deliver
    postgres_changes to it. Stops once the order reaches a terminal state. */
const POLL_MS = 10_000;
const TERMINAL = ['served', 'completed', 'cancelled'];

// Order of the lifecycle, used to light up every step up to the current one.
const STATUS_ORDER = ['received', 'preparing', 'ready', 'served'] as const;

const STEPS = [
  { key: 'received', icon: 'ri-check-double-fill', label: 'Order Received' },
  { key: 'preparing', icon: 'ri-fire-fill', label: 'Preparing' },
  { key: 'ready', icon: 'ri-bell-fill', label: 'Ready to Serve' },
  { key: 'served', icon: 'ri-restaurant-2-fill', label: 'Enjoy!' },
] as const;

const STATUS_HINT: Record<string, string> = {
  received: 'Your order reached the kitchen',
  preparing: 'The kitchen is cooking your food',
  ready: 'Your food is ready!',
  served: 'Served — enjoy your meal',
  cancelled: 'This order was cancelled',
};

// Plays the "order ready" chime (public/sounds/order_ready.mp3) when food is
// ready. Best-effort: browsers may block autoplay until the user has
// interacted — placing the order counts as interaction, so it usually plays.
function playReadyChime() {
  try {
    const audio = new Audio('/sounds/order_ready.mp3');
    audio.volume = 1;
    void audio.play().catch(() => { /* autoplay blocked / unsupported — ignore */ });
  } catch {
    /* Audio unavailable — ignore */
  }
}

export default function OrderConfirmed({ orderId, accessToken, restaurantId, cart, gstRate, gstInclusive = false, onBackToMenu }: OrderConfirmedProps) {
  const [status, setStatus] = useState<string>('received');
  // Server-computed total (authoritative). The browser only shows it.
  const [serverTotal, setServerTotal] = useState<number | null>(null);
  const prevStatusRef = useRef<string>('received');

  /* Live status straight from what the kitchen (KDS) and the waiter (Captain)
     set on the order. Polls the token-gated order_status RPC while the tab is
     open, pauses when the tab is hidden (saves egress), and stops once the
     order is served/cancelled. */
  useEffect(() => {
    if (!orderId || !accessToken) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const applyStatus = (next: string) => {
      setStatus((cur) => {
        // Only advance forward; ignore stale/duplicate reads.
        const ci = STATUS_ORDER.indexOf(cur as typeof STATUS_ORDER[number]);
        const ni = STATUS_ORDER.indexOf(next as typeof STATUS_ORDER[number]);
        const resolved = next === 'cancelled' || ni >= ci ? next : cur;
        if (resolved === 'ready' && prevStatusRef.current !== 'ready') playReadyChime();
        prevStatusRef.current = resolved;
        return resolved;
      });
    };

    const tick = async () => {
      if (!active) return;
      if (!document.hidden) {
        const row = await fetchGuestOrderStatus(orderId, accessToken);
        if (!active) return;
        if (row) {
          if (row.status) applyStatus(row.status);
          if (typeof row.total === 'number' && row.total > 0) setServerTotal(row.total);
          if (TERMINAL.includes(row.status)) return; // done — stop polling
        }
      }
      timer = setTimeout(tick, POLL_MS);
    };

    void tick();
    // Re-check immediately when the diner comes back to the tab. Clear the
    // pending timer first so we never run two polling chains at once.
    const onVisible = () => {
      if (document.hidden) return;
      if (timer) clearTimeout(timer);
      void tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [orderId, accessToken]);

  // Opt-in web push so the diner gets a "your food is ready" notification even
  // if they leave the tab. Best-effort and non-blocking: if the browser asks
  // for permission and the diner declines (or push is unsupported), the in-page
  // live tracker above still updates. Anonymous (no Firebase user).
  useEffect(() => {
    if (!restaurantId) return;
    registerPush('customer', restaurantId).catch(() => { /* push unavailable */ });
  }, [restaurantId]);

  const currentIdx = STATUS_ORDER.indexOf(status as typeof STATUS_ORDER[number]);
  const isReady = status === 'ready';
  const isCancelled = status === 'cancelled';
  const subtotal = cart.reduce((sum, ci) => {
    const price = ci.selectedVariant ? ci.selectedVariant.price : ci.menuItem.price;
    const addonsTotal = ci.selectedAddons.reduce((a, ad) => a + ad.price, 0);
    return sum + (price + addonsTotal) * ci.quantity;
  }, 0);
  // Prefer the server's total; otherwise show a GST-correct local estimate.
  // Inclusive → the menu prices already contain GST, so the total equals the
  // subtotal. Exclusive → GST is added on top (CGST == SGST, half the rate each).
  const gstHalf = Math.round((subtotal * gstRate) / 200);
  const localTotal = gstInclusive ? subtotal : subtotal + gstHalf * 2;
  const total = serverTotal ?? localTotal;
  const itemCount = cart.reduce((s, ci) => s + ci.quantity, 0);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      {/* Success hero */}
      <div className={`px-6 pt-12 pb-10 text-center transition-colors ${isCancelled ? 'bg-red-500' : isReady ? 'bg-green-600' : 'bg-accent-500'}`}>
        {/* Status icon */}
        <div className={`w-20 h-20 mx-auto mb-5 bg-background-50 rounded-full flex items-center justify-center ${isReady ? 'animate-pulse' : 'animate-bounce-in'}`}>
          <i className={`text-4xl ${isCancelled ? 'ri-close-circle-fill text-red-500' : isReady ? 'ri-bell-fill text-green-600' : 'ri-check-double-fill text-accent-500'}`}></i>
        </div>
        <h1 className="text-2xl font-bold text-background-50 font-heading mb-1">
          {isCancelled ? 'Order Cancelled' : isReady ? 'Your Food Is Ready!' : status === 'served' ? 'Enjoy Your Meal!' : 'Order Placed!'}
        </h1>
        <p className="text-accent-100 text-sm">{STATUS_HINT[status] ?? 'Your food is being prepared fresh'}</p>
        <div className="mt-4 inline-block bg-accent-600/40 rounded-xl px-5 py-2.5">
          <p className="text-xs text-accent-100 uppercase tracking-wide">Order ID</p>
          <p className="text-lg font-bold text-background-50 font-heading">{orderId}</p>
        </div>
      </div>

      {/* Order summary */}
      <div className="px-5 -mt-5">
        <div className="bg-background-50 rounded-2xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground-950 font-heading">
            Order Summary · {itemCount} items · ₹{total}
          </h3>
          <div className="divide-y divide-background-200">
            {cart.map((ci, idx) => {
              const price = ci.selectedVariant ? ci.selectedVariant.price : ci.menuItem.price;
              const addonsTotal = ci.selectedAddons.reduce((a, ad) => a + ad.price, 0);

              return (
                <div key={idx} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-background-100">
                    {ci.menuItem.image ? (
                      <img src={ci.menuItem.image} alt={ci.menuItem.name} className="w-full h-full object-cover object-center" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-restaurant-2-line text-sm text-foreground-300"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-950 truncate">{ci.menuItem.name}</p>
                    {ci.selectedVariant && (
                      <p className="text-xs text-foreground-500">{ci.selectedVariant.name} × {ci.quantity}</p>
                    )}
                    {ci.selectedAddons.length > 0 && (
                      <p className="text-[10px] text-foreground-500">+{ci.selectedAddons.map((a) => a.name).join(', ')}</p>
                    )}
                  </div>
                  <span className="text-sm font-bold text-foreground-950 flex-shrink-0">₹{(price + addonsTotal) * ci.quantity}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Status tracker */}
      <div className="px-5 mt-5">
        <h3 className="text-sm font-semibold text-foreground-950 mb-4 font-heading">Order Status</h3>
        <div className="bg-background-50 rounded-2xl p-5">
          <div className="relative">
            {STEPS.map((step, idx) => {
              const done = currentIdx >= idx && !isCancelled;
              const isCurrent = currentIdx === idx && !isCancelled;
              return (
                <div key={step.key} className="flex items-start gap-3 pb-5 last:pb-0 relative">
                  {/* Vertical line */}
                  {idx < STEPS.length - 1 && (
                    <div className={`absolute left-[15px] top-8 w-0.5 h-full ${currentIdx > idx ? 'bg-accent-500' : 'bg-background-200'}`} />
                  )}
                  {/* Icon */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 ${
                      done ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-400'
                    } ${isCurrent ? 'ring-2 ring-accent-300 ring-offset-1' : ''}`}
                  >
                    <i className={`${step.icon} text-sm`}></i>
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${done ? 'text-foreground-950' : 'text-foreground-500'}`}>
                      {step.label}
                    </p>
                    {isCurrent && (
                      <p className="text-xs text-accent-600 font-medium">In progress</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Back CTA */}
      <div className="px-5 py-6 mt-auto">
        <button
          onClick={onBackToMenu}
          className="w-full py-3.5 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-base rounded-xl transition-all active:scale-[0.98] cursor-pointer whitespace-nowrap"
        >
          Order More
        </button>
      </div>

      <style>{`
        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        .animate-bounce-in {
          animation: bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
      `}</style>
    </div>
  );
}
